
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function SMSSignupPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceInterest: '',
    preferredTime: '',
    smsOptIn: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d3mmm3qi723b7tba32n0', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          serviceInterest: '',
          preferredTime: '',
          smsOptIn: true
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-orange-500 via-orange-600 to-red-500 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-smartphone-line text-4xl"></i>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Stay Connected with SMS
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-orange-100">
                Get instant updates about your cleaning appointments, exclusive promotions, and helpful cleaning tips delivered right to your phone
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-lg">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-300 mr-2"></i>
                  Appointment Reminders
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-300 mr-2"></i>
                  Exclusive Offers
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-300 mr-2"></i>
                  Cleaning Tips
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                  Why Choose SMS Updates?
                </h2>
                <p className="text-xl text-gray-600">
                  Stay informed and never miss important updates about your cleaning services
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center p-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-time-line text-orange-500 text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Instant Notifications</h3>
                  <p className="text-gray-600">
                    Receive immediate updates about appointment confirmations, schedule changes, and service completions
                  </p>
                </div>

                <div className="text-center p-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-gift-line text-orange-500 text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Exclusive Offers</h3>
                  <p className="text-gray-600">
                    Be the first to know about special promotions, seasonal discounts, and loyalty rewards
                  </p>
                </div>

                <div className="text-center p-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-lightbulb-line text-orange-500 text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Helpful Tips</h3>
                  <p className="text-gray-600">
                    Get professional cleaning tips and maintenance advice to keep your space looking its best
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Signup Form Section */}
        <section className="py-20 bg-gradient-to-br from-orange-50 to-peach-50">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                  Sign Up for SMS Updates
                </h2>
                <p className="text-xl text-gray-600">
                  Join our SMS list and stay connected with Signature Cleaning Services
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="signup-name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="signup-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="signup-phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="signup-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address (Optional)
                    </label>
                    <input
                      type="email"
                      id="signup-email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="signup-service" className="block text-sm font-medium text-gray-700 mb-2">
                        Service Interest
                      </label>
                      <select
                        id="signup-service"
                        name="serviceInterest"
                        value={formData.serviceInterest}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Select your interest</option>
                        <option value="residential">Residential Cleaning</option>
                        <option value="commercial">Commercial Cleaning</option>
                        <option value="deep-cleaning">Deep Cleaning</option>
                        <option value="move-in-out">Move In/Out Cleaning</option>
                        <option value="post-construction">Post-Construction Cleanup</option>
                        <option value="carpet-cleaning">Carpet Cleaning</option>
                        <option value="window-cleaning">Window Cleaning</option>
                        <option value="all-services">All Services</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="signup-time" className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Contact Time
                      </label>
                      <select
                        id="signup-time"
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Select preferred time</option>
                        <option value="morning">Morning (8AM - 12PM)</option>
                        <option value="afternoon">Afternoon (12PM - 5PM)</option>
                        <option value="evening">Evening (5PM - 8PM)</option>
                        <option value="anytime">Anytime</option>
                      </select>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="signup-smsOptIn"
                        name="smsOptIn"
                        checked={formData.smsOptIn}
                        onChange={handleInputChange}
                        className="mt-1 h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded cursor-pointer"
                      />
                      <div className="flex-1">
                        <label htmlFor="signup-smsOptIn" className="text-sm font-medium text-gray-700 cursor-pointer">
                          SMS Opt In
                        </label>
                        <p className="text-sm text-gray-600 mt-1 mb-3">
                          Opt in to receive SMS notifications, alerts & occasional marketing communication.
                        </p>
                        <div className="text-xs text-gray-500 leading-relaxed space-y-2">
                          <p>
                            By providing your phone number, you agree to receive text messages from Signature Cleaning Services about appointments, updates, and promotions.
                          </p>
                          <p>
                            We won't share your info with third parties for marketing.
                          </p>
                          <p>
                            Message frequency may vary (up to 4–6 messages/month).
                          </p>
                          <p>
                            Reply STOP to unsubscribe at any time.
                          </p>
                          <p>
                            Reply HELP for more info.
                          </p>
                          <p>
                            Message & data rates may apply.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <i className="ri-check-circle-line text-green-500 text-xl mr-3"></i>
                        <p className="text-green-700">Success! You're now signed up for SMS updates. Welcome to our community!</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                        <p className="text-red-700">Sorry, there was an error with your signup. Please try again or contact us directly.</p>
                      </div>
                    </div>
                  )}

                  <div className="text-center">
                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full md:w-auto"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line mr-2 animate-spin"></i>
                          Signing Up...
                        </>
                      ) : (
                        <>
                          <i className="ri-smartphone-line mr-2"></i>
                          Join SMS Updates
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="text-center text-sm text-gray-600 mt-6">
                    <p>
                      Questions about SMS updates? <a href="/#contact" className="text-orange-500 hover:text-orange-600 font-medium">Contact us</a> for more information.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div className="p-6">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-green-500 text-xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">Privacy Protected</h3>
                  <p className="text-sm text-gray-600">Your information is secure and never shared with third parties</p>
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-close-circle-line text-blue-500 text-xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">Easy Unsubscribe</h3>
                  <p className="text-sm text-gray-600">Reply STOP anytime to unsubscribe from SMS updates</p>
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-customer-service-2-line text-orange-500 text-xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">24/7 Support</h3>
                  <p className="text-sm text-gray-600">Need help? Our customer service team is always here for you</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
